import React, { useState, useEffect } from "react";
import { Box, Menu, Typography, useMediaQuery, useTheme } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import Image from "./Image";
import { useTranslation } from "react-i18next";
import * as Constant from "../../features/constant";

const LanguageDropdown = ({ languages }) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const handleLanguageChange = () => {
      localStorage.setItem("language", i18n.language);

      if (process.env.REACT_APP_LANG_PATH_PARAM) {
        let strArr = window.location.pathname.replace("/", "").split("/");
        let pathname = window.location.pathname;
        let langParam = Constant.COUNTRY_LANGUAGE_PATH_PARAMS[process.env.REACT_APP_COUNTRY][i18n.language];

        if (strArr.length > 1) {
          window.location.href = "/" + langParam + "/" + pathname.replace("/" + strArr[0] + "/", "");
        }
        else {
          window.location.href = "/" + langParam + pathname.replace("/" + strArr[0], "");
        }
      }
      else {
        window.location.reload();
      }
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);
  const defaultCountryCode = process.env.REACT_APP_COUNTRY?.toUpperCase();
  const country = Constant.COUNTRY_LANGUAGE_INFO[defaultCountryCode];

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // ✅ Helper to change language + reload immediately
  const handleLanguageSelect = (code) => {
    const newLang = code.toLowerCase();
    localStorage.setItem("language", newLang);
    i18n.changeLanguage(newLang);

    // Delay slightly to allow i18n to update localStorage before reload
    setTimeout(() => {
      if (process.env.REACT_APP_LANG_PATH_PARAM === "true") {
        window.location.href = Constant.getPath("/");
      } else {
        window.location.reload();
      }
    }, 100);
  };

  // 🟦 MOBILE VERSION
  const renderMobileItems = () => (
    <Box display="flex" flexDirection="column" alignItems="flex-start" px={2} py={1}>
      {Object.entries(Constant.COUNTRY_LANGUAGE_INFO).map(([countryCode, country]) => (
        <Box
          key={countryCode}
          width="100%"
          mb={1}
          display="flex"
          alignItems="flex-start"
        >
          <img
            src={country.flag}
            width="45"
            height="45"
            alt={country.name}
            style={{ marginRight: theme.spacing(1), flexShrink: 0 }}
          />

          <Box display="flex" flexDirection="column">
            <Typography
              fontWeight={600}
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || "white",
                lineHeight: 1.2,
              }}
            >
              {country.name}
            </Typography>

            <Box display="flex" alignItems="center" flexWrap="wrap">
              {country.languages.map((language, index) => (
                <React.Fragment key={language.code}>
                  <Typography
                    component="span"
                    sx={{
                      cursor: "pointer",
                      fontSize: country.languages.reduce((sum, l) => sum + l.label.length, 0) > 12 ? "0.75rem" : "0.90rem",
                      color:
                        i18n.language.toUpperCase() === language.code
                          ? theme.palette.primary.main
                          : process.env.REACT_APP_COLOR_TEXT || "white",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() => handleLanguageSelect(language.code)}
                  >
                    {language.label}
                  </Typography>

                  {index < country.languages.length - 1 && (
                    <Typography
                      component="span"
                      px={1}
                      sx={{ color: "white" }}
                    >
                      |
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  // 🟩 DESKTOP VERSION
  const renderDesktopItems = () => (
    <Box display="flex" flexDirection="column" px={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" pt={1}>
        <Image width="30px" height="30px" src={country.flag} />
        <Box display="flex" pl={1}>
          {languages?.map((language) => (
            <Typography
              key={language.code}
              px={1}
              sx={{
                cursor: "pointer",
                color:
                  i18n.language.toUpperCase() === language.code
                    ? theme.palette.primary.main
                    : process.env.REACT_APP_COLOR_TEXT || "white",
              }}
              onClick={() => {
                handleLanguageSelect(language.code);
                handleClose();
              }}
            >
              {language.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {mobileView ? (
        renderMobileItems()
      ) : (
        <>
          {/* Trigger button */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ pl: 1, cursor: "pointer" }}
            onClick={handleOpen}
          >
            <Image width="30px" height="30px" src={country.flag} />
            <KeyboardArrowDown sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }} />
          </Box>

          {/* Menu must be OUTSIDE the trigger Box */}
          <Menu
            sx={{ mt: "40px" }}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {renderDesktopItems()}
          </Menu>
        </>
      )}
    </>
  );
};

export default LanguageDropdown;